import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnInit, untracked } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FieldTree, FormField } from "@angular/forms/signals";
import { AccountType, BrandAsset, MediaType, SbCreativeType } from "@front/m19-api-client";
import { BrandNameMaxLength, headlineLimit } from "@front/m19-models/SbLimits";
import { AccountSelectionService, BrandAssetsService } from "@front/m19-services";
import {
  ConfirmPopupComponent,
  IAlertComponent,
  IErrorComponent,
  IFormFieldComponent,
  IHintDirective,
  IInputDirective,
  ILabelComponent,
  ModalService,
  ToasterService,
  IIconComponent,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { MediaInputComponent } from "@m19-board/shared/media/media-input/media-input.component";
import { UploadAmazonLoadingModalComponent } from "@m19-board/shared/media/upload-amazon-loading-modal/upload-amazon-loading-modal.component";
import { FormPreviewSplitComponent } from "@m19-board/shared/form-preview-split/form-preview-split.component";
import { CreativePreviewListComponent } from "@m19-board/strategies/sb-create-strategy/creative-preview-list/creative-preview-list.component";
import { SbPreModerationService } from "@m19-board/strategies/sb-pre-moderation/sb-pre-moderation.service";
import { Subject, switchMap } from "rxjs";

export interface SbFormAssetsModel {
  brandName: string;
  headline: string;
  brandLogo: BrandAsset | null;
  video: BrandAsset | null;
}

@Component({
  selector: "app-sb-form-assets",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    FormField,
    IFormFieldComponent,
    IInputDirective,
    ILabelComponent,
    IErrorComponent,
    IAlertComponent,
    MediaInputComponent,
    CreativePreviewListComponent,
    FormPreviewSplitComponent,
    IHintDirective,
    IIconComponent,
  ],
  host: {
    class: "flex h-full min-h-0 flex-col",
  },
  templateUrl: "./sb-form-assets.component.html",
})
export class SbFormAssetsComponent implements OnInit {
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly brandAssetsService = inject(BrandAssetsService);
  private readonly modalService = inject(ModalService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  readonly preModeration = inject(SbPreModerationService);

  readonly assets = input.required<FieldTree<SbFormAssetsModel>>();
  readonly creativeType = input.required<SbCreativeType>();
  readonly brandEntityId = input<string>();
  readonly defaultBrandName = input<string>();

  readonly MediaType = MediaType;

  readonly resetVideoInput = new Subject<void>();

  private readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });
  readonly isVendor = computed(() => this.accountMarketplace().accountType === AccountType.VENDOR);

  readonly headlineMaxLength = computed(() =>
    headlineLimit(this.creativeType(), this.accountMarketplace().marketplace),
  );

  readonly showBrandFields = computed(() => this.creativeType() !== SbCreativeType.video);
  readonly showVideo = computed(
    () => this.creativeType() === SbCreativeType.video || this.creativeType() === SbCreativeType.brandVideo,
  );
  readonly displayEmptyHeadlineBrandLogoHint = computed(
    () =>
      this.creativeType() === SbCreativeType.manualCollection ||
      this.creativeType() === SbCreativeType.productCollection,
  );

  private readonly brandAssets = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.brandAssetsService.getBrandAssetsForSb(am.accountId, am.marketplace)),
    ),
  );
  readonly brandAssetsLoaded = computed(() => this.brandAssets() !== undefined);

  readonly brandLogos = computed(
    () =>
      this.brandAssets()?.brandLogos?.filter(
        (asset) =>
          asset.mediaType === MediaType.brandLogo &&
          !asset.deleted &&
          (this.isVendor() || asset.brandEntityId === this.brandEntityId()),
      ) ?? [],
  );

  readonly videos = computed(
    () =>
      this.brandAssets()
        ?.videos?.filter((asset) => asset.mediaType === MediaType.videoAsset)
        .filter(
          (video) =>
            this.creativeType() !== SbCreativeType.video ||
            (video.width == undefined && video.height == undefined) ||
            video.width > video.height,
        )
        .sort((a, b) => {
          if (a.url && !b.url) return -1;
          if (!a.url && b.url) return 1;
          return a.name > b.name ? 1 : -1;
        }) ?? [],
  );

  readonly showHeadlineMaxLengthHint = computed(
    () =>
      !this.assets()
        .headline()
        .errors()
        .some((error) => error.kind === "headlineTooLong"),
  );

  readonly headlineErrorMessage = computed(() => {
    const error = this.assets().headline().errors()[0];
    if (!error) return "";
    if (error.kind === "headlineTooLong") {
      return this.translocoService.translate("sb-form-creative.headline_max_length", [this.headlineMaxLength()]);
    }
    if (error.kind === "required") {
      return this.translocoService.translate("sb-form-creative.headline");
    }
    return error.message ? this.translocoService.translate(error.message) : "";
  });

  readonly brandNameErrorMessage = computed(() => {
    const error = this.assets().brandName().errors()[0];
    if (!error) return "";
    if (error.kind === "invalidName") {
      return this.translocoService.translate("sb-form-creative.brand_name_contains_invalid_characters");
    }
    return this.translocoService.translate("sb-form-creative.brand_name");
  });

  constructor() {
    effect(() => {
      const defaultName = this.defaultBrandName();
      if (!defaultName || !this.showBrandFields()) return;
      untracked(() => {
        if (!this.assets().brandName().value()) {
          this.assets().brandName().value.set(defaultName.substring(0, BrandNameMaxLength));
        }
      });
    });
  }

  ngOnInit() {
    if (this.creativeType() === SbCreativeType.productCollection) {
      this.assets().headline().markAsTouched();
    }
  }

  selectBrandLogo(asset: BrandAsset | null | undefined) {
    this.assets()
      .brandLogo()
      .value.set(asset ?? null);
    this.assets().brandLogo().markAsDirty();
  }

  selectVideo(asset: BrandAsset | null | undefined) {
    this.assets()
      .video()
      .value.set(asset ?? null);
    this.assets().video().markAsDirty();
  }

  onUpload(file: File, mediaType: MediaType) {
    const am = this.accountMarketplace();
    const ref = this.modalService.openModal(UploadAmazonLoadingModalComponent, {
      closeMode: "none",
    });

    this.brandAssetsService
      .uploadBrandAsset({
        accountId: am.accountId,
        marketplace: am.marketplace,
        brandEntityId: this.isVendor() ? "" : (this.brandEntityId() ?? ""),
        mediaType,
        file,
      })
      .subscribe({
        next: (asset) => {
          ref.close();
          if (!asset) return;
          if (mediaType === MediaType.videoAsset && this.creativeType() === SbCreativeType.video) {
            if (asset.height != null && asset.width != null && asset.height > asset.width) {
              this.resetVideoInput.next();
              this.modalService.openModal(ConfirmPopupComponent, {
                modalTitle: this.translocoService.translate("sb-form-creative.vertical_videos_not_supported"),
                data: {
                  message: this.translocoService.translate("sb-form-creative.vertical_videos_not_supported_message"),
                },
              });
              return;
            }
          }
          if (mediaType === MediaType.brandLogo) {
            this.selectBrandLogo(asset);
          } else {
            this.selectVideo(asset);
          }
          this.toasterService.success(
            this.translocoService.translate("sb-form-creative.asset_uploaded_successfully_to_amazon"),
          );
        },
        error: (error) => {
          ref.close();
          this.toasterService.error(this.translocoService.translate("sb-form-creative.error_uploading_asset"), error);
        },
      });
  }
}
